import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { tokenService } from './tokenService';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 900000, // 15 minutes in milliseconds
});

// Queue for failed requests during token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from tokenService
    const authHeader = tokenService.getAuthHeader();
    if (authHeader) {
      config.headers.Authorization = authHeader;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for login and refresh endpoints
      if (originalRequest.url === API_ENDPOINTS.AUTH.LOGIN || 
          originalRequest.url === API_ENDPOINTS.AUTH.REFRESH) {
        tokenService.clearAll();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = tokenService.getAuthHeader();
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenService.getRefreshToken();
      
      if (!refreshToken) {
        // No refresh token available
        isRefreshing = false;
        tokenService.clearAll();
        processQueue(error, null);
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const { access_token, refresh_token: new_refresh_token, token_type, expires_at } = response.data;
        
        // Update tokens
        tokenService.setTokens(access_token, new_refresh_token, token_type, expires_at);
        
        // Process queued requests
        processQueue(null, access_token);
        
        // Retry original request
        originalRequest.headers.Authorization = tokenService.getAuthHeader();
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed
        processQueue(refreshError, null);
        tokenService.clearAll();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    if (error.response) {
      switch (error.response.status) {
        case 403:
          console.error('Access forbidden:', error.response.data);
          break;
        case 404:
          console.error('Resource not found:', error.response.data);
          break;
        case 500:
          console.error('Server error:', error.response.data);
          break;
        default:
          if (error.response.status !== 401) {
            console.error('API error:', error.response.data);
          }
      }
    } else if (error.request) {
      console.error('Network error - no response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
  }
);

// Helper function to get auth headers (for FormData requests)
export const getAuthHeaders = () => {
  const authHeader = tokenService.getAuthHeader();
  return authHeader ? { Authorization: authHeader } : {};
};

// Export the configured instance
export default api;